import type { TFunction } from 'i18next';

import { ApiError } from '../api/types';

type Translate = TFunction;

/** Turns any thrown value into a sentence a player can act on. */
export function apiErrorMessage(error: unknown, t: Translate): string {
  if (!(error instanceof ApiError)) return t('errors.generic');
  if (error.status === 0) return t('errors.network');
  if (error.isBanned) return t('errors.bannedBody');
  if (error.isRateLimited) {
    return error.retryAfterSeconds
      ? t('errors.rateLimited', { seconds: error.retryAfterSeconds })
      : t('errors.rateLimitedShort');
  }
  if (error.isMaintenance) return t('errors.maintenanceBody');
  if (error.isFeatureDisabled) return t('errors.featureDisabled');
  if (error.normalizedCode === 'EMAIL_NOT_VERIFIED') return t('auth.emailNotVerified');
  if (error.normalizedCode === 'MAIL_NOT_CONFIGURED' || error.normalizedCode === 'MAIL_SEND_FAILED') {
    return t('auth.mailUnavailable');
  }
  // Wrong password is 401 INVALID_CREDENTIALS, not "please sign in".
  if (error.normalizedCode === 'INVALID_CREDENTIALS') return t('auth.invalidCredentials');
  if (error.isUnauthorized) return t('errors.unauthorized');
  if (error.isForbidden) return t('errors.forbidden');
  if (error.status === 404) return t('errors.notFoundBody');
  if (Object.keys(error.fieldErrors).length > 0) {
    const firstField = Object.values(error.fieldErrors)[0];
    return firstField || error.message || t('errors.validation');
  }
  return error.message || t('errors.generic');
}

/**
 * Maps server field errors onto form fields. The contract sends localized
 * sentences (arrays of strings), not machine codes.
 */
export function fieldErrors(error: unknown, _t?: Translate): Record<string, string> {
  if (!(error instanceof ApiError)) return {};
  const mapped: Record<string, string> = {};
  for (const [field, message] of Object.entries(error.fieldErrors)) {
    const camel = field.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());
    mapped[field] = message;
    mapped[camel] = message;
  }
  return mapped;
}

export const isApiError = (error: unknown): error is ApiError => error instanceof ApiError;
